/**
 * SurveyResultDetailPage — per-question results for one completed survey (VER-109).
 *
 * Route: /survey/results/:resultId
 *
 * Shows an overall score summary (for scored scales) then per-question analytics.
 * FRONTEND-ONLY: reads the mock COMPLETED_SURVEYS and aggregates client-side via
 * aggregateSurvey(). No API.
 *
 * TODO [RBAC]: guarded with permission="DashboardView" in Routes.tsx — swap to the
 * survey-results permit once defined. Run verbal-rbac-review before merge.
 */
import React from "react";
import { Box, Button, Chip, Divider, LinearProgress, Stack, Typography } from "@mui/material";
import { ArrowBackOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { SimpleBlankSlate } from "../../../core/components/SimpleBlankSlate";
import { VCard } from "../../../core/components/VCard";
import { findCompletedSurvey } from "./surveyResults.mock";
import { aggregateSurvey, OverallScore } from "./aggregate";
import { QuestionResultCard } from "./QuestionResultCard";
import { ExportMenu } from "./ExportMenu";

const formatDate = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? iso
        : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const OverallScoreCard = ({
    overall,
    higherIsBetter,
}: {
    overall: OverallScore;
    higherIsBetter: boolean;
}): JSX.Element => {
    const { meanFinal, completeCount, rawRange, multiplier } = overall;
    const finalMax = rawRange[1] * (multiplier ?? 1);
    const finalMin = rawRange[0] * (multiplier ?? 1);
    const isPercent = multiplier != null && finalMax === 100;
    const pct = meanFinal !== null && finalMax > finalMin ? (meanFinal - finalMin) / (finalMax - finalMin) : 0;

    return (
        <VCard variant="outlined" sx={{ mb: 3 }}>
            <Typography variant="overline" color="textSecondary">
                Overall score · {higherIsBetter ? "higher is better" : "lower is better"}
            </Typography>
            <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
                <Typography variant="h3" sx={{ color: "primary.main", fontWeight: 700 }}>
                    {meanFinal === null ? "—" : `${meanFinal.toFixed(1)}${isPercent ? "%" : ""}`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    mean{isPercent ? "" : ` (out of ${finalMax})`} · {completeCount} complete{" "}
                    {completeCount === 1 ? "response" : "responses"}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, Math.round(pct * 100)))}
                sx={{ height: 10, borderRadius: 1 }}
            />
        </VCard>
    );
};

export const SurveyResultDetailPage = (): JSX.Element => {
    const { resultId } = useParams<{ resultId: string }>();
    const navigate = useNavigate();
    const completed = resultId ? findCompletedSurvey(resultId) : undefined;

    if (!completed) {
        return (
            <Box p={4} width={1}>
                <Button
                    variant="text"
                    color="primary"
                    startIcon={<ArrowBackOutlined />}
                    onClick={() => navigate("/survey/results")}
                    sx={{ mb: 2 }}
                >
                    All results
                </Button>
                <SimpleBlankSlate header="Result not found" content="This survey result does not exist." />
            </Box>
        );
    }

    const { survey, responses, programmeName, completedAt } = completed;
    const analytics = aggregateSurvey(survey, responses);

    return (
        <Box p={4} width={1}>
            <Button
                variant="text"
                color="primary"
                startIcon={<ArrowBackOutlined />}
                onClick={() => navigate("/survey/results")}
                sx={{ mb: 2 }}
            >
                All results
            </Button>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
                mb={0.5}
            >
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h3" component="h1">
                        {survey.title}
                    </Typography>
                    {survey.code ? <Chip size="small" variant="outlined" label={survey.code} /> : null}
                </Stack>
                <ExportMenu completed={completed} variant="button" />
            </Stack>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {programmeName} · completed {formatDate(completedAt)} · {responses.length} responses
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {analytics.scored && analytics.overall && (
                <OverallScoreCard overall={analytics.overall} higherIsBetter={survey.higherIsBetter} />
            )}

            {analytics.questions.map((result, i) => (
                <QuestionResultCard key={result.question.id} result={result} index={i + 1} />
            ))}
        </Box>
    );
};
